// Corporate Account service — API_INTEGRATION.md §3.34
// Manages corporate/business accounts for bulk ordering.
// Vertical: SHARED. No feature flag.

import { apiClient, apiClientPaginated, apiBlob, ApiError } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface CorporateAccount {
  id: string;
  tenant_id: string;
  company_name: string;
  gstin: string | null;
  contact_name: string;
  contact_phone: string;
  contact_email: string | null;
  credit_limit: number; // paise
  outstanding_balance: number; // paise — invoiced, not yet paid
  /** Placed / confirmed / delivered orders not yet invoiced (paise). */
  open_orders_total: number;
  /** credit_limit − outstanding − open orders (paise); negative when over the limit. */
  available_credit: number;
  billing_address: string | null;
  is_active: boolean;
  total_orders: number;
  created_at: string;
  updated_at: string;
}

export interface CorporateOrder {
  id: string;
  corporate_account_id: string;
  location_id: string;
  location_name: string;
  order_number: string;
  // The list endpoint returns item_count only; the detail endpoint returns the
  // full items array. items is therefore optional on list rows.
  items?: Array<{
    product_id: string;
    product_name: string;
    quantity: number;
    unit_price: number; // paise
    gst_rate: number;
    line_total: number; // paise
  }>;
  item_count?: number;
  total: number; // paise
  tax_total: number; // paise
  delivery_date: string | null;
  notes: string | null;
  status: CorporateOrderStatus;
  invoice_number: string | null;
  created_at: string;
}

export type CorporateOrderStatus = 'PLACED' | 'CONFIRMED' | 'DELIVERED' | 'INVOICED' | 'CANCELLED';

/** Lifecycle steps (owner decision C8): Confirm → Deliver → Invoice; Cancel before Deliver. */
export type CorporateOrderAction = 'confirm' | 'deliver' | 'invoice' | 'cancel';

export interface CorporateInvoice {
  id: string;
  order_id: string;
  invoice_number: string;
  order_number: string;
  amount: number; // paise
  tax_amount: number; // paise
  paid_amount: number; // paise
  balance: number; // paise
  status: 'UNPAID' | 'PARTIALLY_PAID' | 'PAID';
  created_at: string;
}

export type CorporatePaymentMethod = 'CASH' | 'UPI' | 'CARD' | 'BANK_TRANSFER';

export interface CorporatePayment {
  id: string;
  order_id: string;
  invoice_number: string;
  amount: number; // paise
  method: CorporatePaymentMethod;
  reference: string | null;
  paid_at: string;
}

export interface RecordCorporatePaymentDto {
  order_id: string;
  amount: number; // paise
  method: CorporatePaymentMethod;
  reference?: string;
}

export interface CreateCorporateAccountDto {
  company_name: string;
  gstin?: string;
  contact_name: string;
  contact_phone: string;
  contact_email?: string;
  /** Credit limit in paise. 0 = NO credit; positive = that limit. Required in the form. */
  credit_limit?: number;
  billing_address?: string;
}

export interface CreateCorporateOrderDto {
  corporate_account_id: string;
  location_id: string;
  items: Array<{ product_id: string; quantity: number }>;
  delivery_date?: string;
  notes?: string;
  /** Owner approval for an order over the credit limit (QA CU3-001). */
  approval_token?: string;
}

export interface CorporateAccountListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

// ─── Service ────────────────────────────────────────────────────────────────

export const corporateAccountService = {
  async list(params: CorporateAccountListParams = {}): Promise<{
    data: CorporateAccount[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const q = buildQuery({
      page: params.page,
      limit: params.limit ?? 20,
      search: params.search,
      status: params.status,
    });
    return apiClientPaginated<CorporateAccount[]>(`/corporate-accounts${q}`);
  },

  async get(id: string): Promise<CorporateAccount> {
    return apiClient<CorporateAccount>(`/corporate-accounts/${id}`);
  },

  async create(dto: CreateCorporateAccountDto): Promise<CorporateAccount> {
    return apiClient<CorporateAccount>('/corporate-accounts', { method: 'POST', body: dto });
  },

  async update(id: string, dto: Partial<CreateCorporateAccountDto>): Promise<CorporateAccount> {
    return apiClient<CorporateAccount>(`/corporate-accounts/${id}`, { method: 'PATCH', body: dto });
  },

  async deactivate(id: string): Promise<CorporateAccount> {
    return apiClient<CorporateAccount>(`/corporate-accounts/${id}`, { method: 'PATCH', body: { is_active: false } });
  },

  async listOrders(accountId: string, params?: { page?: number }): Promise<{
    data: CorporateOrder[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const q = buildQuery({ page: params?.page, limit: 20, corporate_account_id: accountId });
    return apiClientPaginated<CorporateOrder[]>(`/corporate-orders${q}`);
  },

  async createOrder(dto: CreateCorporateOrderDto): Promise<CorporateOrder> {
    return apiClient<CorporateOrder>('/corporate-orders', { method: 'POST', body: dto });
  },

  async listInvoices(accountId: string): Promise<CorporateInvoice[]> {
    return apiClient<CorporateInvoice[]>(`/corporate-accounts/${accountId}/invoices`);
  },

  async downloadInvoice(orderId: string): Promise<Blob> {
    const { blob } = await apiBlob(`/corporate-orders/${orderId}/invoice`);
    return blob;
  },

  async transitionOrder(orderId: string, action: CorporateOrderAction): Promise<CorporateOrder> {
    return apiClient<CorporateOrder>(`/corporate-orders/${orderId}/${action}`, { method: 'POST' });
  },

  async listPayments(accountId: string): Promise<CorporatePayment[]> {
    return apiClient<CorporatePayment[]>(`/corporate-accounts/${accountId}/payments`);
  },

  async recordPayment(accountId: string, dto: RecordCorporatePaymentDto): Promise<CorporatePayment> {
    return apiClient<CorporatePayment>(`/corporate-accounts/${accountId}/payments`, { method: 'POST', body: dto });
  },
};

export { ApiError };
